import { useState } from "react";
import { Link } from "wouter";
import { sugerirTipoCliente, type MotivoSugestao } from "@eurostorm/shared";
import { useT, type MessageKey } from "../../i18n/index.js";
import { trpc } from "../../lib/trpc.js";

/**
 * CEO 2026-10-27 (opção C) — fila "Clientes a revisar": o cliente que entrou
 * pelas listas do eDSP chega com o tipo SUGERIDO; aqui o escritório confirma
 * tipo, remise e chantier numa tela só. Confirmar tira da fila e, se algo
 * mudou, refaz remise e franquia dos carros ainda sem fatura.
 */
export interface ClienteParaRevisar {
  id: number;
  displayName: string;
  legalName?: string | null;
  kind: string;
  fiscalId?: string | null;
  vatNumber?: string | null;
  defaultRetentionPct?: string | number | null;
  chantierId?: number | null;
  openDossiers?: number | null;
}

const MOTIVO_KEY: Record<MotivoSugestao, MessageKey> = {
  siret_tva: "customerReview.reasonSiretTva",
  nome_empresa: "customerReview.reasonNomeEmpresa",
  nome_pessoa: "customerReview.reasonNomePessoa",
  indefinido: "customerReview.reasonIndefinido",
};

const AVISO = { background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" };

export function CustomerReviewModal({
  companyId,
  customer,
  onClose,
  showOpenRecord = true,
}: {
  companyId: number;
  customer: ClienteParaRevisar;
  onClose: () => void;
  showOpenRecord?: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const sug = sugerirTipoCliente({
    nome: customer.displayName,
    siret: customer.fiscalId,
    tva: customer.vatNumber,
    razaoSocial: customer.legalName,
  });
  const atual = customer.kind === "individual" ? "individual" : "business";
  const [kind, setKind] = useState<"individual" | "business">(
    sug.motivo === "indefinido" ? atual : sug.kind,
  );
  const [remise, setRemise] = useState(
    customer.defaultRetentionPct != null ? String(Number(customer.defaultRetentionPct)) : "",
  );
  const [chantierId, setChantierId] = useState<number | null>(customer.chantierId ?? null);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState<number | null>(null);
  const chantiersQ = trpc.chantiers.list.useQuery({ companyId, activeOnly: true });
  const mut = trpc.customers.confirmReview.useMutation({
    onSuccess: (r) => {
      void utils.customers.invalidate();
      void utils.home.invalidate();
      void utils.dossiers.invalidate();
      setFeito(r.carrosRefeitos);
    },
    onError: (e) =>
      setErro(e.message === "REVIEW_REMISE_REQUIRED" ? t("customerReview.errRemise") : e.message),
  });

  const confirmar = () => {
    setErro(null);
    const n = remise.trim() === "" ? null : Number(remise.replace(",", "."));
    if (kind === "business" && (n == null || !Number.isFinite(n) || n < 0 || n > 100)) {
      setErro(t("customerReview.errRemise"));
      return;
    }
    mut.mutate({
      companyId,
      customerId: customer.id,
      kind,
      defaultRetentionPct: kind === "business" ? n : null,
      chantierId,
    });
  };

  const campo = "w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-1.5 text-sm";
  const opcao = (k: "individual" | "business", rotulo: MessageKey, dica: MessageKey) => (
    <label
      className={`flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm ${
        kind === k ? "border-lime" : "border-neutral-700"
      }`}
    >
      <input
        type="radio"
        name="review-kind"
        checked={kind === k}
        onChange={() => setKind(k)}
        className="mt-0.5"
        data-testid={`review-kind-${k}`}
      />
      <span>
        <span className="font-semibold">{t(rotulo)}</span>
        {sug.kind === k && sug.motivo !== "indefinido" && (
          <span className="ml-1 rounded bg-lime/20 px-1.5 py-0.5 text-[10px] font-semibold text-lime">
            ★
          </span>
        )}
        <span className="block text-xs text-neutral-500">{t(dica)}</span>
      </span>
    </label>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      onClick={onClose}
      data-testid="customer-review-modal"
    >
      <div
        className="w-full max-w-lg space-y-4 rounded-lg border border-neutral-800 bg-neutral-900 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-neutral-500">
              🔎 {t("customerReview.title")}
            </p>
            <h2 className="break-words text-lg font-semibold">{customer.displayName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-2 py-0.5 text-sm"
            aria-label="×"
          >
            ×
          </button>
        </div>

        {feito != null ? (
          <div className="space-y-3" data-testid="customer-review-done">
            <p className="text-sm text-lime">
              ✓ {t("customerReview.doneMsg")}
              {feito > 0 && ` ${t("customerReview.doneCarsPre")} ${feito}.`}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm"
            >
              ×
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs text-neutral-500">{t("customerReview.intro")}</p>
            <p className="rounded-md border px-3 py-2 text-sm" style={AVISO}>
              {t("customerReview.suggestionPre")}{" "}
              <strong>
                {sug.kind === "individual"
                  ? t("customerReview.kindIndividual")
                  : t("customerReview.kindBusiness")}
              </strong>{" "}
              ({t(MOTIVO_KEY[sug.motivo])})
            </p>

            <fieldset className="space-y-2">
              {opcao(
                "individual",
                "customerReview.kindIndividual",
                "customerReview.kindIndividualHint",
              )}
              {opcao("business", "customerReview.kindBusiness", "customerReview.kindBusinessHint")}
            </fieldset>

            {kind === "business" && (
              <label className="block text-xs text-neutral-400">
                {t("customerReview.remiseLabel")}
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={remise}
                  onChange={(e) => setRemise(e.target.value)}
                  className={`${campo} mt-1`}
                  data-testid="review-remise"
                />
              </label>
            )}

            <label className="block text-xs text-neutral-400">
              {t("customerReview.chantierLabel")}
              <select
                value={chantierId ?? ""}
                onChange={(e) => setChantierId(e.target.value ? Number(e.target.value) : null)}
                className={`${campo} mt-1`}
              >
                <option value="">{t("customerReview.chantierNone")}</option>
                {(chantiersQ.data?.rows ?? []).map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.name}
                  </option>
                ))}
              </select>
            </label>

            {(customer.openDossiers ?? 0) > 0 && (
              <p className="text-xs text-neutral-500">
                {t("customerReview.openCarsPre")} <strong>{customer.openDossiers}</strong> —{" "}
                {t("customerReview.openCarsHint")}
              </p>
            )}

            {erro && <p className="text-sm text-red-500">{erro}</p>}

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-800 pt-3">
              {showOpenRecord ? (
                <Link
                  href={`/clientes/${customer.id}`}
                  className="text-xs text-neutral-500 underline hover:text-lime"
                >
                  {t("customerReview.openRecord")}
                </Link>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm"
                >
                  {t("customerReview.cancel")}
                </button>
                <button
                  type="button"
                  onClick={confirmar}
                  disabled={mut.isPending}
                  className="rounded-md bg-lime px-4 py-1.5 text-sm font-semibold text-on-lime hover:bg-lime-bright disabled:opacity-40"
                  data-testid="review-confirm"
                >
                  {mut.isPending ? "…" : t("customerReview.confirm")}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
